import {
  BookmarkIcon,
  CameraIcon,
  FunnelIcon,
  HeartIcon,
  PhotoIcon,
  SparklesIcon,
  StarIcon,
  SunIcon,
  TagIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { savedFilterIconSchema, type SavedFilterIcon } from '@xphoto/shared';
import type { MessageKey } from '../i18n';

/**
 * Correspondance NOM D'ICÔNE (allow-list partagée `savedFilterIconSchema`) → composant
 * Heroicon outline 24px (SVG inline, jamais d'emoji ni d'icon-font) et clé i18n de son nom
 * lisible (pour l'a11y du sélecteur). Un filtre enregistré sans icône retombe sur
 * `FunnelIcon` (entonnoir = « filtre ») à l'affichage.
 */

/** Type d'un composant Heroicon outline (tous les icônes partagent la même signature). */
type IconComponent = typeof FunnelIcon;

const ICON_COMPONENTS: Record<SavedFilterIcon, IconComponent> = {
  funnel: FunnelIcon,
  star: StarIcon,
  camera: CameraIcon,
  'video-camera': VideoCameraIcon,
  sparkles: SparklesIcon,
  tag: TagIcon,
  sun: SunIcon,
  heart: HeartIcon,
  bookmark: BookmarkIcon,
  photo: PhotoIcon,
};

/** Clé i18n du NOM lisible de chaque icône (labels a11y du sélecteur). */
export const SAVED_FILTER_ICON_LABEL_KEY: Record<SavedFilterIcon, MessageKey> = {
  funnel: 'savedFilters.iconName.funnel',
  star: 'savedFilters.iconName.star',
  camera: 'savedFilters.iconName.camera',
  'video-camera': 'savedFilters.iconName.videoCamera',
  sparkles: 'savedFilters.iconName.sparkles',
  tag: 'savedFilters.iconName.tag',
  sun: 'savedFilters.iconName.sun',
  heart: 'savedFilters.iconName.heart',
  bookmark: 'savedFilters.iconName.bookmark',
  photo: 'savedFilters.iconName.photo',
};

/** Ordre d'affichage du sélecteur d'icône (ordre de l'allow-list partagée). */
export const SAVED_FILTER_ICON_ORDER: readonly SavedFilterIcon[] = savedFilterIconSchema.options;

/**
 * Rend l'icône d'un filtre enregistré. `icon` `null`/absent ⇒ entonnoir (défaut « filtre »).
 * Toujours `aria-hidden` : le sens est porté par le NOM textuel du filtre à côté.
 */
export function SavedFilterGlyph({
  icon,
  className,
}: {
  icon: SavedFilterIcon | null;
  className?: string;
}): React.JSX.Element {
  const Component = ICON_COMPONENTS[icon ?? 'funnel'];
  return <Component className={className} aria-hidden="true" />;
}
